import { useState, useImperativeHandle, forwardRef } from 'react';
import clsx from 'clsx';

import { Button } from '@appkit4/react-components/button';
import { Avatar } from '@appkit4/react-components/avatar';
import { DropdownButton, ValueType } from '@appkit4/react-components/dropdown';
import { Modal } from '@appkit4/react-components/modal';
import { Input } from '@appkit4/react-components';
import { Tooltip } from '@appkit4/react-components/tooltip';
import ShareModal from '../ShareModal/ShareModal';
import RemoveUserModal from '../RemoveUserModal/RemoveUserModal';
import DuplicateModal from '../DuplicateModal/DuplicateModal';

import { generateTimestamp } from '@utils/generateTimestamp';
import { getEmailInitials, getFullNameFromEmail } from '@utils/users';
import { deleteSession, removeShare, duplicateSession } from '@config/getSessionApis';

import { ShareUser } from '@contexts/SessionProvider';

import Success from '@assets/images/Success.svg';
import styles from './SessionListItem.module.scss';
import DesignSystemBadge from '../DesignSystemBadge';

interface iSessionListItem {
    userEmail: string;
    sessionId: string;
    sessionName: string;
    lastEdited: number;
    favorite: string;
    ownerEmail?: string;
    designSystem: string;
    isSharedSession?: boolean;
    isGenerateComplete?: boolean;
    onToggleFavorite?: () => void;
    onUpdateSessionName?: (name: string) => void;
    onDeleteSession?: (sessionId: string) => void;
    onClickSession?: () => void;
    onDuplicateSession?: () => void;
}

const SessionListItem = forwardRef(
    (
        {
            userEmail,
            sessionId,
            sessionName,
            lastEdited,
            favorite,
            ownerEmail,
            designSystem,
            isSharedSession,
            isGenerateComplete = true,
            onToggleFavorite,
            onUpdateSessionName,
            onDeleteSession,
            onClickSession,
            onDuplicateSession,
            ...props
        }: iSessionListItem,
        ref: any
    ) => {
        const isReadOnly = !!ownerEmail && ownerEmail !== userEmail;
        const isGenerating = !isGenerateComplete;
        const [visible, setVisible] = useState(false);
        const [isEditing, setIsEditing] = useState(false);
        const [topic, setTopic] = useState(sessionName);
        const [shareModalVisible, setShareModalVisible] = useState(false);
        const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
        const [removeUserModalVisible, setRemoveUserModalVisible] = useState(false);
        const [deleteSuccessVisible, setDeleteSuccessVisible] = useState(false);
        const [userRemove, setUserRemove] = useState({} as ShareUser);
        useImperativeHandle(ref, () => ({
            setIsEditing,
        }));

        const handleContextSelect = async (value: ValueType, _: any, event: any) => {
            event.stopPropagation();

            if (value === 'share' && isReadOnly) {
                return;
            }
            if (value === 'rename' && (isReadOnly || isGenerating)) {
                return;
            }
            if (value === 'duplicate' && isGenerating) {
                return;
            }

            if (value === 'rename') {
                setIsEditing(true);
            } else if (value === 'duplicate') {
                handleDuplicate();
            } else if (value === 'delete') {
                setVisible(true);
            } else {
                setShareModalVisible(true);
            }
        };

        const handleDuplicate = async () => {
            try {
                setDuplicateModalVisible(true);
                await duplicateSession(sessionId);
                setDuplicateModalVisible(false);

                if (onDuplicateSession) {
                    onDuplicateSession();
                }
            } catch (error) {
                console.error('Error duplicating session:', error);
                setDuplicateModalVisible(false);
            }
        };

        const handleInputSessionName = (event: any) => {
            event?.stopPropagation();
        };

        const onChangeSessionName = (value: string) => {
            setTopic(value);
        };

        const onBlurSessionName = (event: any) => {
            if (event.target.value.trim() === '') {
                setTopic(sessionName);
                setIsEditing(false);
                return;
            }

            if (onUpdateSessionName) {
                onUpdateSessionName(topic.trim());
            }
        };

        const handleDeleteSession = async () => {
            if (isReadOnly) {
                try {
                    const shareRemove = await removeShare(userEmail, sessionId);
                    if (shareRemove.code === 1) {
                        setVisible(false);
                        setDeleteSuccessVisible(true);
                        if (onDeleteSession) {
                            onDeleteSession(sessionId);
                        }
                    }
                } catch {
                    console.log('Error removing user from session');
                }
            } else {
                try {
                    const deleteResponse = await deleteSession(userEmail, sessionId);
                    if (deleteResponse.code === 1) {
                        setVisible(false);
                        setDeleteSuccessVisible(true);
                        if (onDeleteSession) {
                            onDeleteSession(sessionId);
                        }
                    }
                } catch {
                    console.error('Error deleting session');
                }
            }
        };

        const handleClickSession = (event: any) => {
            event?.stopPropagation();
            if (onClickSession) {
                onClickSession();
            }
        };

        const handleToggleFavorite = (event: any) => {
            event.stopPropagation();

            if (onToggleFavorite) {
                onToggleFavorite();
            }
        };

        const renderContextItem = (label: React.ReactNode, item: any) => {
            return (
                <div className={styles['context-item']}>
                    <span className={`Appkit4-icon ${item.iconName}`} />
                    <span>{label}</span>
                </div>
            );
        };

        const handleShareModalCancel = () => {
            setShareModalVisible(false);
        };

        const handleDuplicateModalCancel = () => {
            setDuplicateModalVisible(false);
        };

        const handleRemoveUserModalCancel = () => {
            setRemoveUserModalVisible(false);
            setUserRemove({} as ShareUser);
            setShareModalVisible(true);
        };

        const handleRemoveUserConfirm = (user: any) => {
            // remove user
            setRemoveUserModalVisible(false);
            setUserRemove(user);
            setShareModalVisible(true);
        };

        const confirmModal = visible && (
            <Modal
                visible={visible}
                title={'Delete project'}
                ariaLabel={'Delete project'}
                onCancel={() => setVisible(false)}
                modalStyle={{ width: '33.75rem' }}
                footerStyle={{ paddingTop: '8px', marginTop: '-8px', minHeight: '64px' }}
                header={''}
                icons={''}
                footer={
                    <>
                        <Button onClick={() => setVisible(false)} kind="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteSession} kind="negative">
                            Delete
                        </Button>
                    </>
                }
                bodyStyle={{ minHeight: '60px' }}>
                <div className={styles['confirm-modal-body']}>
                    Are you sure you want to delete this project? This action is permanent.
                </div>
            </Modal>
        );

        const deleteSuccessModal = deleteSuccessVisible && (
            <Modal
                visible={deleteSuccessVisible}
                title={''}
                ariaLabel={'Delete project successfully'}
                onCancel={() => setDeleteSuccessVisible(false)}
                modalStyle={{ width: '500px' }}
                footerStyle={{ paddingTop: '8px', marginTop: '-8px', minHeight: '64px' }}
                className="delete-success-modal"
                header={''}
                icons={''}
                footer={
                    <>
                        <Button style={{ width: '100%' }} onClick={() => setDeleteSuccessVisible(false)} kind="primary">
                            Close
                        </Button>
                    </>
                }
                bodyStyle={{ minHeight: '60px' }}>
                <div className={styles['confirm-modal-body']}>
                    <Success />
                    <div className="ap-typography-heading-m ap-mt-spacing-6">Success!!</div>
                    <div className="ap-typography-body ap-mt-spacing-4">
                        Your project has been deleted successfully.
                    </div>
                </div>
            </Modal>
        );

        return (
            <>
                <div className={styles['session-list-item']} {...props} onClick={handleClickSession}>
                    {isEditing ? (
                        <>
                            <Input
                                type={'text'}
                                hideTitleOnInput={true}
                                inputProps={{
                                    autoFocus: true,
                                }}
                                onClick={handleInputSessionName}
                                value={topic}
                                onChange={onChangeSessionName}
                                onBlur={onBlurSessionName}
                            />
                        </>
                    ) : (
                        <>
                            <div className={styles['content-side']}>
                                <div className={styles['name-wrapper']}>{sessionName}</div>
                            </div>
                            <div className={styles.timestamp}>{generateTimestamp(lastEdited)}</div>
                        </>
                    )}
                    <div className={styles['button-wrapper']}>
                        {isReadOnly && (
                            <Tooltip
                                trigger="hover"
                                position="top"
                                distance={4}
                                appendAfterTarget={false}
                                content={getFullNameFromEmail(ownerEmail || '')}>
                                <Avatar
                                    label={getEmailInitials(ownerEmail || '')}
                                    role="button"
                                    disabled={false}
                                    style={{ width: '24px', height: '24px', fontSize: '10.5px', fontWeight: '300' }}
                                />
                            </Tooltip>
                        )}
                        {(designSystem === 'Appkit' ||
                            designSystem === 'AppkitRebrand' ||
                            designSystem === 'Fluent' ||
                            designSystem === 'MaterialUI' ||
                            designSystem === 'NewEra') && (
                            <DesignSystemBadge designSystem={designSystem} size="sm" showName={true} />
                        )}
                        <div onClick={(e) => e.stopPropagation()}>
                            <DropdownButton
                                className={styles['context-button']}
                                kind="text"
                                onSelect={handleContextSelect}
                                itemTemplate={renderContextItem}
                                ariaLabel="Context button"
                                data={[
                                    {
                                        label: 'Share',
                                        value: 'share',
                                        iconName: 'icon-share-outline',
                                        disabled: isReadOnly,
                                    },
                                    {
                                        label: 'Duplicate',
                                        value: 'duplicate',
                                        iconName: 'icon-duplicate-outline',
                                        disabled: isGenerating,
                                    },
                                    {
                                        label: 'Rename',
                                        value: 'rename',
                                        iconName: 'icon-edit-outline',
                                        disabled: isReadOnly || isGenerating,
                                    },
                                    { label: 'Delete', value: 'delete', iconName: 'icon-delete-outline' },
                                ]}>
                                <span className="Appkit4-icon icon-circle-more-outline" />
                            </DropdownButton>
                        </div>
                        {!isSharedSession && (
                            <Button kind="text" onClick={handleToggleFavorite} className={styles['favorite-button']}>
                                <span
                                    className={clsx(
                                        'Appkit4-icon',
                                        favorite ? 'icon-star-fill' : 'icon-star-outline'
                                    )}></span>
                            </Button>
                        )}
                    </div>
                </div>
                {confirmModal}
                {deleteSuccessVisible && deleteSuccessModal}
                {shareModalVisible && (
                    <ShareModal
                        visible={shareModalVisible}
                        title="Share project"
                        onCancel={handleShareModalCancel}
                        modalStyle={{ width: '500px' }}
                        activeSessionId={sessionId}></ShareModal>
                )}
                {removeUserModalVisible && (
                    <RemoveUserModal
                        visible={removeUserModalVisible}
                        title="Remove user?"
                        onCancel={handleRemoveUserModalCancel}
                        onRemoveConfirm={(user: any) => handleRemoveUserConfirm(user)}
                        modalStyle={{ width: '540px' }}
                        userRemove={userRemove}></RemoveUserModal>
                )}
                {duplicateModalVisible && (
                    <DuplicateModal
                        visible={duplicateModalVisible}
                        title="Duplicating file"
                        onCancel={handleDuplicateModalCancel}
                        modalStyle={{ width: '440px' }}></DuplicateModal>
                )}
            </>
        );
    }
);

export { SessionListItem };
